import { useCallback, useMemo, useRef, useState } from 'react';

import { useAddEmailAttachmentDraftMutation } from 'core/graphql';

import {
  EMAIL_ATTACHMENTS_HINT_BYTES,
  EMAIL_FILE_HARD_MAX_BYTES,
  IRichMailError,
  richMailError,
} from './richMailModel';

export type EmailAttachmentStatus = 'uploading' | 'done' | 'error';

export interface IEmailAttachmentChip {
  /** Ключ строки на клиенте (id черновика появляется после загрузки). */
  key: string;
  /** Id черновика вложения на сервере (`EmailAttachmentDraftType.id`). */
  id: string | null;
  name: string;
  size: number;
  status: EmailAttachmentStatus;
  error: IRichMailError | null;
}

let seq = 0;
const nextKey = () => {
  seq += 1;
  return `att-${seq}`;
};

/**
 * ТЗ 0097 — вложения письма: каждый файл сразу уходит черновиком на сервер
 * (`uploadEmailAttachment`, один файл на вызов — отказ по одному файлу не
 * роняет остальные), в письмо идут id черновиков. Пока что-то грузится —
 * отправлять нельзя; сумму сверх обычного предела только предупреждаем.
 */
export const useEmailAttachments = () => {
  const [items, setItems] = useState<IEmailAttachmentChip[]>([]);
  const [uploadMut] = useAddEmailAttachmentDraftMutation();
  // Убранный во время загрузки файл не должен «воскреснуть», когда ответ
  // придёт: помним ключи, которые ещё в списке.
  const alive = useRef(new Set<string>());

  const patch = useCallback(
    (key: string, next: Partial<IEmailAttachmentChip>) =>
      setItems((list) =>
        list.map((item) => (item.key === key ? { ...item, ...next } : item)),
      ),
    [],
  );

  const add = useCallback(
    async (file: File) => {
      const key = nextKey();
      alive.current.add(key);
      const tooLarge = file.size > EMAIL_FILE_HARD_MAX_BYTES;
      setItems((list) => [
        ...list,
        {
          key,
          id: null,
          name: file.name,
          size: file.size,
          status: tooLarge ? 'error' : 'uploading',
          error: tooLarge ? { code: 'FILE_TOO_LARGE', message: '' } : null,
        },
      ]);
      if (tooLarge) return;
      try {
        const { data } = await uploadMut({ variables: { file } });
        const draft = data?.uploadEmailAttachment;
        if (!alive.current.has(key)) return;
        if (!draft) {
          patch(key, {
            status: 'error',
            error: { code: 'INVALID_FILE', message: '' },
          });
          return;
        }
        patch(key, {
          id: draft.id,
          name: draft.name,
          size: draft.size,
          status: 'done',
        });
      } catch (caught) {
        if (!alive.current.has(key)) return;
        patch(key, { status: 'error', error: richMailError(caught) });
      }
    },
    [patch, uploadMut],
  );

  const remove = useCallback((key: string) => {
    alive.current.delete(key);
    setItems((list) => list.filter((item) => item.key !== key));
  }, []);

  const clear = useCallback(() => {
    alive.current.clear();
    setItems([]);
  }, []);

  const summary = useMemo(() => {
    const done = items.filter((item) => item.status === 'done');
    const totalBytes = done.reduce((sum, item) => sum + item.size, 0);
    return {
      ids: done.map((item) => item.id as string),
      totalBytes,
      uploading: items.some((item) => item.status === 'uploading'),
      /** Больше обычного предела сервера — предупреждение, не запрет. */
      overHint: totalBytes > EMAIL_ATTACHMENTS_HINT_BYTES,
    };
  }, [items]);

  return { items, add, remove, clear, ...summary };
};

export type IEmailAttachments = ReturnType<typeof useEmailAttachments>;
